// Taki panosu — Trendyol ve Etsy taki magazalari icin ORTAK sayfa.
//
// Trendyol panosunun mantigi: magazanin kendi ucundan urun/siparis ozeti +
// uretim zincirinin kisayollari (Katalog → Studio → Taslaklar → Sablonlar).
// Etsy taki magazasi da ayni mantikla calisir; tek fark ozet ucu ve para birimi.
// Tekstil panosundaki tasarim havuzu / eRank kavramlari burada yoktur.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Package, ShoppingCart, AlertCircle, Store, Warehouse, Sparkles, RefreshCw, FileText } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";

interface Summary {
  shop?: { id: string | null; shopName: string; externalShopId: string; vertical?: string };
  productCount: number | null;
  productError: string | null;
  orderCount: number | null;
  orderError: string | null;
  recentRevenue: number;
  periodDays: number;
}

const VERTICAL_LABEL: Record<string, string> = { apparel: "Tekstil", giftware: "Hediyelik", jewelry: "Takı" };

export default function JewelryDashboardPage() {
  const { shop, platform, vertical } = useShopVertical();
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isTrendyol = platform === "trendyol";
  // Etsy'de bu pano yalnizca taki dikeyi icindir; tekstil kendi panosunda kalir
  const supported = isTrendyol || vertical === "jewelry";
  const summaryUrl = isTrendyol ? "/api/trendyol/summary" : "/api/etsy/summary";

  async function load() {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${getBackendUrl()}${summaryUrl}`, { headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Özet alınamadı");
      setData(body.data as Summary);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Özet alınamadı");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (supported) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, vertical, shop?.id]);

  if (!supported) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <h1 className="text-sm font-semibold text-amber-900">Takı mağazası seçili değil</h1>
          <p className="mt-1 text-sm text-amber-800">
            Bu pano Trendyol mağazaları ve Etsy takı mağazaları içindir. Üst bardan uygun bir mağaza seçin.
          </p>
          <Link to="/my-shops" className="mt-3 inline-block rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600">
            Mağazalarım
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <span className={`flex h-6 w-6 items-center justify-center rounded text-xs font-bold text-white ${isTrendyol ? "bg-orange-500" : "bg-[#f1641e]"}`}>
              {isTrendyol ? "t" : "E"}
            </span>
            {shop?.shopName || (isTrendyol ? "Trendyol" : "Etsy")}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isTrendyol ? "Trendyol" : "Etsy"} mağaza panosu · {VERTICAL_LABEL[vertical] ?? vertical}
            {data?.shop?.externalShopId ? ` · Mağaza #${data.shop.externalShopId}` : ""}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Yenile
        </button>
      </div>

      {error && (
        <p className="mb-4 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </p>
      )}

      {/* Ozet kartlari — bir uc patlarsa digeri yine gosterilir */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          icon={<Package className="h-4 w-4 text-indigo-500" />}
          label={isTrendyol ? "Trendyol'daki ürün" : "Etsy'deki aktif liste"}
          value={loading ? null : data?.productCount ?? null}
          error={data?.productError}
        />
        <StatCard
          icon={<ShoppingCart className="h-4 w-4 text-emerald-500" />}
          label={`Son ${data?.periodDays ?? 30} gün sipariş`}
          value={loading ? null : data?.orderCount ?? null}
          error={data?.orderError}
        />
        <StatCard
          icon={<span className="text-sm font-bold text-amber-500">{isTrendyol ? "₺" : "$"}</span>}
          label="Son siparişlerin cirosu"
          value={loading ? null : data?.recentRevenue ?? null}
          suffix={isTrendyol ? " TL" : " $"}
          hint="Son 50 siparişin toplamı"
        />
      </div>

      {/* Kisayollar — taki uretim zinciri */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ShortcutCard
          to="/supplier-catalogs"
          icon={<Warehouse className="h-5 w-5 text-indigo-600" />}
          title="Katalog"
          desc="Tedarikçi ürünleri, pazaryerine uygun olanlar ve sabit satış fiyatları"
        />
        <ShortcutCard
          to="/jewelry-studio"
          icon={<Sparkles className="h-5 w-5 text-pink-600" />}
          title="Studio"
          desc="Konsept fotoğrafındaki ürünü katalogdaki ürünle değiştir"
        />
        <ShortcutCard
          to="/listing-drafts"
          icon={<FileText className="h-5 w-5 text-blue-600" />}
          title="Taslaklar"
          desc="Katalog ürününden oluşturulan liste taslakları"
        />
        <ShortcutCard
          to="/templates"
          icon={<Package className="h-5 w-5 text-emerald-600" />}
          title="Şablonlar"
          desc="Liste ve varyasyon şablonları"
        />
        <ShortcutCard
          to="/my-shops"
          icon={<Store className="h-5 w-5 text-orange-600" />}
          title="Mağazalarım"
          desc="Bağlantı bilgileri, dikey ve mağaza ayarları"
        />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, error, suffix, hint }: {
  icon: React.ReactNode; label: string; value: number | null; error?: string | null; suffix?: string; hint?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500">{icon}{label}</div>
      {error ? (
        <p className="mt-1 text-xs text-red-600" title={error}>Alınamadı</p>
      ) : (
        <p className="mt-1 text-2xl font-bold text-gray-900">
          {value === null ? <Loader2 className="h-5 w-5 animate-spin text-gray-300" /> : `${value.toLocaleString("tr-TR")}${suffix ?? ""}`}
        </p>
      )}
      {hint && !error && <p className="mt-0.5 text-[10px] text-gray-400">{hint}</p>}
    </div>
  );
}

function ShortcutCard({ to, icon, title, desc }: { to: string; icon: React.ReactNode; title: string; desc: string }) {
  return (
    <Link to={to} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-indigo-300 hover:bg-indigo-50/30">
      <div className="mt-0.5">{icon}</div>
      <div>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="mt-0.5 text-xs text-gray-500">{desc}</p>
      </div>
    </Link>
  );
}
